import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { Box, FormControl, IconButton, MenuItem, Select } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import type { FilterControlProps, FilterOperator } from '../filters/filter-registry.js';
import {
  FILTER_OPERATORS,
  getOperatorsForType,
  registerFilterControl,
} from '../filters/filter-registry.js';
import { FilterOperatorMenu } from './FilterOperatorMenu.tsx';

type EnumFilterProps = FilterControlProps;

function EnumColumnFilter({ column, value, onChange, disabled }: EnumFilterProps) {
  const { t } = useTranslation();
  const fieldType = column.fieldType === 'status' ? 'status' : 'select';
  const allowedOperators = useMemo(() => {
    const candidates = getOperatorsForType(fieldType);
    const allowed = column.allowedFilterOperators;
    return allowed ? candidates.filter(operator => allowed.includes(operator.id)) : candidates;
  }, [column.allowedFilterOperators, fieldType]);
  const defaultOperator = allowedOperators.find(operator => operator.id === 'equals')?.id
    ?? allowedOperators[0]?.id
    ?? 'equals';
  const [localOperator, setLocalOperator] = React.useState<FilterOperator>(value?.operator ?? defaultOperator);
  const operatorDescriptor =
    FILTER_OPERATORS.find(operator => operator.id === localOperator) ?? allowedOperators[0];
  const isMulti = localOperator === 'in' || localOperator === 'notIn';
  const requiresValue = operatorDescriptor?.requiresValue !== false;
  const enumOptions = column.enumOptions ?? [];
  const [localValue, setLocalValue] = React.useState<string | string[]>(
    isMulti ? [] : '',
  );

  useEffect(() => {
    if (value) {
      setLocalOperator(value.operator);
      const nextMulti = value.operator === 'in' || value.operator === 'notIn';
      if (nextMulti) {
        setLocalValue(Array.isArray(value.value) ? value.value.map(String) : [String(value.value ?? '')].filter(Boolean));
      } else {
        setLocalValue(Array.isArray(value.value) ? String(value.value[0] ?? '') : String(value.value ?? ''));
      }
    } else {
      setLocalOperator(defaultOperator);
      setLocalValue(defaultOperator === 'in' || defaultOperator === 'notIn' ? [] : '');
    }
  }, [defaultOperator, value]);

  const emit = (operator: FilterOperator, nextValue: string | string[]) => {
    const descriptor = FILTER_OPERATORS.find(candidate => candidate.id === operator);
    if (descriptor?.requiresValue === false) {
      onChange({ operator, value: null });
      return;
    }
    const empty = Array.isArray(nextValue) ? nextValue.length === 0 : nextValue === '';
    onChange(empty ? null : { operator, value: nextValue });
  };

  const handleOperatorChange = (operator: string) => {
    const nextOperator = operator as FilterOperator;
    const nextMulti = nextOperator === 'in' || nextOperator === 'notIn';
    const normalized = nextMulti
      ? (Array.isArray(localValue) ? localValue : [localValue].filter(Boolean))
      : (Array.isArray(localValue) ? localValue[0] ?? '' : localValue);
    setLocalOperator(nextOperator);
    setLocalValue(normalized);
    emit(nextOperator, normalized);
  };

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const raw = event.target.value;
    const normalized = isMulti
      ? (Array.isArray(raw) ? raw : [raw])
      : (Array.isArray(raw) ? raw[0] ?? '' : raw);
    setLocalValue(normalized);
    emit(localOperator, normalized);
  };

  const handleClear = () => {
    const empty = isMulti ? [] : '';
    setLocalValue(empty);
    onChange(null);
  };

  const columnLabel = t(resolveTKey(column.labelKey));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {allowedOperators.length > 1 && (
        <FilterOperatorMenu
          column={column}
          currentOperator={localOperator}
          onChange={handleOperatorChange}
          allowedOperators={allowedOperators}
          disabled={disabled}
        />
      )}
      {requiresValue && (
        <FormControl fullWidth size="small" disabled={disabled} sx={{ minWidth: 0 }}>
          <Select
            aria-label={columnLabel}
            multiple={isMulti}
            value={localValue}
            onChange={handleChange}
            displayEmpty
            renderValue={selected => {
              const values = Array.isArray(selected) ? selected : [selected];
              if (values.length === 0 || (values.length === 1 && values[0] === '')) {
                return <Box component="span" sx={{ color: 'text.secondary' }}>{columnLabel}</Box>;
              }
              const labels = values.map(raw => {
                const option = enumOptions.find(candidate => candidate.value === raw);
                return option ? t(resolveTKey(option.labelKey)) : raw;
              });
              return labels.join(', ');
            }}
          >
            {!isMulti && (
              <MenuItem value="">
                <Box component="span" sx={{ color: 'text.secondary' }}>{columnLabel}</Box>
              </MenuItem>
            )}
            {enumOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {t(resolveTKey(option.labelKey))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {((Array.isArray(localValue) && localValue.length > 0) || (!Array.isArray(localValue) && localValue) || !requiresValue) && (
        <IconButton
          size="small"
          onClick={handleClear}
          disabled={disabled}
          aria-label={t('common:clear')}
          sx={{ flexShrink: 0 }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function createEnumFilter() {
  return React.memo(EnumColumnFilter);
}

registerFilterControl('select', () => createEnumFilter());
registerFilterControl('status', () => createEnumFilter());
